import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// Mesmos números de dias da cartilha/manual do cliente (src/lib/manualCliente.js) —
// o painel calcula o próximo prazo em cima do que já está marcado no checklist do caso.
const DIAS_DOCS = 3;               // etapa 3: cliente envia documentação após assinar
const DIAS_ATE_NOTIFICACAO = 30;   // etapas 4+5+6: solicitação (10) + retorno da instituição (10) + notificação (10)
const DIAS_ATUALIZACAO = 15;       // etapas 7/8: cadência de atualização durante tratativas/judicial

function hojeISO() { return new Date().toISOString().split('T')[0]; }
function somaDias(dataISO, dias) {
  const d = new Date(dataISO + 'T00:00');
  d.setDate(d.getDate() + dias);
  return d.toISOString().split('T')[0];
}
function diasEntre(dataISO) {
  const hoje = new Date(hojeISO() + 'T00:00');
  const alvo = new Date(dataISO + 'T00:00');
  return Math.round((alvo - hoje) / 86400000);
}
function fmtData(d) { return d ? new Date(d + 'T00:00').toLocaleDateString('pt-BR') : '—'; }

// Determina o próximo prazo em aberto de um caso, com base no marco mais avançado já
// concluído no checklist. Retorna null se o caso não tem prazo processual ativo
// (ainda sem contrato, ou já com sentença/concluído).
function calcularPrazo(caso, ultimaAtualizacaoISO) {
  if (caso.dt_sentenca) return null;

  if (caso.dt_acao_protocolada) {
    const base = ultimaAtualizacaoISO || caso.dt_acao_protocolada;
    return { tipo: 'Atualização quinzenal (Judicial)', responsavel: 'equipe', dataLimite: somaDias(base, DIAS_ATUALIZACAO) };
  }
  if (caso.dt_notificacao_enviada) {
    const base = ultimaAtualizacaoISO || caso.dt_notificacao_enviada;
    return { tipo: 'Atualização quinzenal (tratativas)', responsavel: 'equipe', dataLimite: somaDias(base, DIAS_ATUALIZACAO) };
  }
  if (caso.dt_documentos_entregues) {
    return { tipo: 'Enviar notificação extrajudicial', responsavel: 'equipe', dataLimite: somaDias(caso.dt_documentos_entregues, DIAS_ATE_NOTIFICACAO) };
  }
  if (caso.dt_contrato_assinado) {
    return { tipo: 'Cliente enviar documentação', responsavel: 'cliente', dataLimite: somaDias(caso.dt_contrato_assinado, DIAS_DOCS) };
  }
  return null;
}

const RESP_INFO = {
  equipe:  { label: 'Nosso prazo', cor: '#7B2FBE' },
  cliente: { label: 'Depende do cliente', cor: '#C9A84C' },
};

export default function Prazos() {
  const navigate = useNavigate();
  const [linhas, setLinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [filtroResp, setFiltroResp] = useState('');

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setCarregando(true);
    const [{ data: casos }, { data: projetos }, { data: atualizacoes }] = await Promise.all([
      supabase.from('casos').select('id, nome_cliente, projeto_id, dt_contrato_assinado, dt_documentos_entregues, dt_notificacao_enviada, dt_acao_protocolada, dt_sentenca'),
      supabase.from('projetos').select('id, nome, cor'),
      supabase.from('caso_atualizacoes').select('caso_id, created_at').order('created_at', { ascending: false }),
    ]);

    const ultimaPorCaso = {};
    (atualizacoes || []).forEach(a => {
      if (!ultimaPorCaso[a.caso_id]) ultimaPorCaso[a.caso_id] = a.created_at.split('T')[0];
    });
    const projetoPorId = Object.fromEntries((projetos || []).map(p => [p.id, p]));

    const calc = (casos || [])
      .map(c => {
        const prazo = calcularPrazo(c, ultimaPorCaso[c.id]);
        if (!prazo) return null;
        return {
          casoId: c.id,
          nomeCliente: c.nome_cliente,
          projeto: projetoPorId[c.projeto_id],
          ...prazo,
          diasRestantes: diasEntre(prazo.dataLimite),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.diasRestantes - b.diasRestantes);

    setLinhas(calc);
    setCarregando(false);
  }

  const linhasFiltradas = linhas.filter(l => !filtroResp || l.responsavel === filtroResp);
  const atrasados = linhas.filter(l => l.diasRestantes < 0).length;
  const proximos = linhas.filter(l => l.diasRestantes >= 0 && l.diasRestantes <= 3).length;

  if (carregando) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Carregando...</div>;

  return (
    <div>
      <div className="section-header"><h2 className="section-title">Painel de Prazos</h2><div className="section-line" /></div>
      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Calculado a partir do checklist de cada caso, com os mesmos prazos do manual que o cliente recebe (3 dias pra documentação, 30 dias até a notificação extrajudicial, atualização a cada 15 dias em tratativas/judicial). Casos sem contrato assinado, ou já com sentença, não aparecem aqui.
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div className="card" style={{ padding: '14px 20px', flex: '1 1 160px' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#dc2626', fontFamily: 'Playfair Display, serif' }}>{atrasados}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>atrasados</div>
        </div>
        <div className="card" style={{ padding: '14px 20px', flex: '1 1 160px' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#C9A84C', fontFamily: 'Playfair Display, serif' }}>{proximos}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>vencem em até 3 dias</div>
        </div>
        <div className="card" style={{ padding: '14px 20px', flex: '1 1 160px' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--purple)', fontFamily: 'Playfair Display, serif' }}>{linhas.length}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>casos com prazo ativo</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button onClick={() => setFiltroResp('')} className={`tab-btn${filtroResp === '' ? ' active' : ''}`}>Todos</button>
        <button onClick={() => setFiltroResp('equipe')} className={`tab-btn${filtroResp === 'equipe' ? ' active' : ''}`}>Nosso prazo</button>
        <button onClick={() => setFiltroResp('cliente')} className={`tab-btn${filtroResp === 'cliente' ? ' active' : ''}`}>Depende do cliente</button>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        {linhasFiltradas.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Nenhum prazo ativo por aqui — tudo em dia.</div>}
        {linhasFiltradas.map((l, i) => {
          const atrasado = l.diasRestantes < 0;
          const urgente = l.diasRestantes >= 0 && l.diasRestantes <= 3;
          const resp = RESP_INFO[l.responsavel];
          return (
            <div key={l.casoId} onClick={() => navigate(`/casos?abrir=${l.casoId}`)}
              style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: 14, alignItems: 'center', padding: '13px 18px', cursor: 'pointer', borderBottom: i < linhasFiltradas.length - 1 ? '1px solid rgba(107,33,168,0.06)' : 'none', background: atrasado ? '#fff8f8' : 'transparent', borderLeft: atrasado ? '3px solid #dc2626' : urgente ? '3px solid #C9A84C' : '3px solid transparent' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{l.nomeCliente}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: l.projeto?.cor || 'var(--purple)', display: 'inline-block' }} />
                  {l.projeto?.nome || '—'}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{l.tipo}</div>
              <span style={{ background: resp.cor + '22', color: resp.cor, padding: '3px 10px', borderRadius: 20, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{resp.label}</span>
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>até {fmtData(l.dataLimite)}</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: atrasado ? '#dc2626' : urgente ? '#92400e' : '#16a34a', whiteSpace: 'nowrap', textAlign: 'right' }}>
                {atrasado ? `⚠ ${Math.abs(l.diasRestantes)}d atrasado` : l.diasRestantes === 0 ? 'vence hoje' : `${l.diasRestantes}d restantes`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
